// ── Auth state management ──

import { api, apiFetch, clearToken, setAuthToken, HAD_SESSION_KEY } from './api.js';
import { navigate } from './router.js';
import { buildSignupBody, buildProfilePatchBody } from './lib/signup-form.js';

// Dispatched on `window` after a successful PATCH /auth/me (updateProfile()
// below) — detail: { user }. Joins the auth:expired/auth:refreshed/
// auth:cancelled family (api.js). `user` can be null in the pathological case
// where the session died between the PATCH and the verifySession() probe —
// listeners must tolerate that. Sole listener today: components/navbar.js's
// refreshNavbarUser(), since mountNavbar() only ever renders the user once
// per session (TPT81).
const USER_UPDATED_EVENT = 'auth:user-updated';

let currentUser = null;

async function checkAuth() {
  if (currentUser) return true;
  try {
    const result = await api.get('/auth/me');
    currentUser = result.user;
    // A live session here can mean either a localStorage JWT (already marks
    // HAD_SESSION_KEY via setAuthToken) or the httpOnly Google cookie, which
    // never touches localStorage on its own — mark it here so a returning
    // Google user still gets "Session expired" instead of a silent bounce
    // to sign-in if the cookie later lapses (C1249).
    localStorage.setItem(HAD_SESSION_KEY, '1');
    return true;
  } catch {
    currentUser = null;
    navigate('/login');
    return false;
  }
}

// Silent session re-check — used by the login screen after an OAuth redirect
// lands (JWT arrives as an httpOnly cookie, not a URL param/hash, so there is
// nothing to parse from the URL; re-verifying against /auth/me is the only
// way to detect the return). Unlike checkAuth(), never redirects or pops the
// reauth modal on failure — the login screen is already the right place to be.
async function verifySession() {
  try {
    const result = await apiFetch('/auth/me', { _silent: true });
    currentUser = result.user;
    localStorage.setItem(HAD_SESSION_KEY, '1'); // see checkAuth() above
    return true;
  } catch {
    currentUser = null;
    return false;
  }
}

function getUser() { return currentUser; }
function isAuthenticated() { return !!currentUser; }

async function logout() {
  try { await api.post('/auth/logout'); } catch { /* ignore */ }
  clearToken();
  // Explicit logout is the one case where "no session" should NOT trigger
  // "Session expired" on the next 401 — the user chose to sign out, so the
  // next visit should look exactly like a first-time visitor's (C1249).
  localStorage.removeItem(HAD_SESSION_KEY);
  currentUser = null;
  navigate('/login');
}

// Email+password sign-in. `_silent` — a wrong password is a normal form error,
// not a "session expired" event, so it must not trigger the reauth modal (api.js
// pops that on any non-silent 401). No 2FA on the account → session is live,
// same as a successful Google login. 2FA on the account → server withholds the
// cookie/token entirely and hands back a short-lived temp_token instead; caller
// must collect a code and finish via completeTwoFactorLogin().
async function loginWithPassword(email, password) {
  const res = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    _silent: true,
  });
  if (res.requires_2fa) return { requires2fa: true, tempToken: res.temp_token };
  setAuthToken(res.token);
  await verifySession();
  return { requires2fa: false };
}

// Second step of a 2FA login — temp_token (from loginWithPassword) + either the
// 6-digit authenticator code or a one-time backup code (C1140). Same _silent
// reasoning as above: a wrong/expired code is a form error the caller
// re-prompts for, not a real session expiry.
async function completeTwoFactorLogin(tempToken, code) {
  const res = await apiFetch('/auth/2fa/login', {
    method: 'POST',
    body: JSON.stringify({ temp_token: tempToken, code }),
    _silent: true,
  });
  setAuthToken(res.token);
  await verifySession();
  return {
    backupCodeUsed: !!res.backup_code_used,
    backupCodesRemaining: res.backup_codes_remaining,
  };
}

// Create an email+password account. Never 401s (unauthenticated by design), so
// the normal api.post() is fine here — its error path already surfaces
// err.message/err.status for 400 (validation) and 409 (email taken / google_only).
// No longer called by the SPA (C1496 replaced the one-step signup pane with
// the verified-email wizard below) — kept exported since the server route
// (POST /auth/register) still exists and is unrelated API surface.
async function registerWithPassword({ email, password, name }) {
  const res = await api.post('/auth/register', { email, password, name });
  setAuthToken(res.token);
  await verifySession();
}

// ── Signup wizard (C1494/C1495/C1496) ──
// Three steps: prove the email (start/verify), then complete the profile.
// All three use `_silent: true` — none of them is a "your session expired"
// event, and `signupComplete` in particular MUST stay silent: a bad/expired
// signup token 401s, and a non-silent 401 runs api.js's _triggerReauth(),
// which pops the "Session expired" modal or hash-redirects to #/login —
// either would blow away a half-filled wizard. Same reasoning as
// loginWithPassword/completeTwoFactorLogin above. Every error already
// surfaces via apiFetch's existing err.message/err.status/err.body — none of
// these wrap or rethrow.

// POST /auth/signup/start — validates the email shape, upserts+emails a
// 6-digit code. Returns { expiresAt } (ISO string, ~15 min out). Calling this
// again for the same address is how "resend" works — there is no separate
// resend endpoint.
//
// `betaCode` (TPT96) is optional — omitted entirely when blank, matching
// buildSignupBody's "a present key is a value to validate" contract. The
// server re-checks it (or a pending project invite, or beta mode being off)
// on every call; a blank betaCode here is fine for an invited user (no code
// needed) and simply fails the gate for anyone else.
async function signupStart(email, betaCode = '') {
  const body = betaCode ? { email, beta_code: betaCode } : { email };
  const res = await apiFetch('/auth/signup/start', {
    method: 'POST',
    body: JSON.stringify(body),
    _silent: true,
  });
  return { expiresAt: res.expires_at };
}

// POST /auth/signup/verify — checks the 6-digit code. Returns a short-lived
// (30 min) signup token that authenticates nothing on its own; the caller
// must hold it in memory only (never localStorage/sessionStorage) and pass it
// to signupComplete. `code` is passed through as typed (trimmed) — the server
// normalizes spaces/dashes itself.
async function signupVerify(email, code) {
  const res = await apiFetch('/auth/signup/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
    _silent: true,
  });
  return { signupToken: res.signup_token, email: res.email, expiresIn: res.expires_in };
}

// POST /auth/signup/complete — the third and final step: spends the signup
// token to actually create the account. `fields`: { name?, password,
// avatar?, headline?, background?, experience_years? } — buildSignupBody()
// (lib/signup-form.js) omits a blank optional entirely rather than sending
// ''/null, since the server treats any *present* key as a value to validate.
// Same setAuthToken+verifySession tail as every other successful sign-in, so
// the caller can navigate immediately after.
// `betaCode` (TPT96) is optional, same omit-when-blank contract as
// signupStart above — this is the call that actually consumes the code
// server-side once the account is created.
async function signupComplete(signupToken, fields, betaCode = '') {
  const body = buildSignupBody(signupToken, fields);
  if (betaCode) body.beta_code = betaCode;
  const res = await apiFetch('/auth/signup/complete', {
    method: 'POST',
    body: JSON.stringify(body),
    _silent: true,
  });
  setAuthToken(res.token);
  await verifySession();
  return res.user;
}

// ── Security-page actions (C1140) ──
// All of these confirm with a current password / TOTP code / backup code,
// which the caller can get wrong — a wrong confirmation is a 401 form error,
// not a session expiry, so every call here goes through _silent (same
// reasoning as loginWithPassword/completeTwoFactorLogin above). Getting this
// wrong pops the Google "Session expired" modal on a simple typo.

// Confirm the first code from an authenticator app; returns the one-time
// backup codes to show the user (only ever handed back here, once).
async function verifyTwoFactorSetup(code) {
  const res = await apiFetch('/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
    _silent: true,
  });
  await verifySession();
  return res.backup_codes;
}

// Turn 2FA off. confirm: { password? } or { code? } (TOTP or backup code).
async function disableTwoFactor(confirm) {
  await apiFetch('/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify(confirm),
    _silent: true,
  });
  await verifySession();
}

// Regenerate the backup code set. confirm: { password? } or { code? } (TOTP
// only — a backup code can't authorise replacing itself). Returns the new
// plaintext codes.
async function regenerateBackupCodes(confirm) {
  const res = await apiFetch('/auth/2fa/backup-codes', {
    method: 'POST',
    body: JSON.stringify(confirm),
    _silent: true,
  });
  await verifySession();
  return res.backup_codes;
}

// Set a password on a Google-only account, or change an existing one.
// `current_password` only needed when the account already has a password;
// `code` only needed when 2FA is enabled.
async function setAccountPassword({ password, current_password, code }) {
  await apiFetch('/auth/password', {
    method: 'POST',
    body: JSON.stringify({ password, current_password, code }),
    _silent: true,
  });
  await verifySession();
}

// ── Profile page (TPT81) ──
// PATCH /auth/me — name/headline/background/experience_years/avatar, no
// password or 2FA confirmation (a display name and picture aren't sensitive
// enough for that ceremony; email/password/TOTP keep their own endpoints).
// `fields` is the raw form shape; buildProfilePatchBody() (lib/signup-form.js)
// owns the clear-vs-omit encoding — read its comment before changing a
// caller. `fields.avatar` is tri-state: undefined = unchanged, null = clear,
// string = new data URL.
//
// `_silent` for the same reason as every sibling confirmation-style call in
// this file: the profile page owns its own inline error line, and a 401
// mid-edit must not pop the reauth modal over a half-typed bio — the caller
// handles a 401 itself.
//
// verifySession() rather than caching res.user directly: `currentUser` has no
// exported setter, and re-probing /auth/me is what every other mutating
// helper in this file does (it also re-stamps HAD_SESSION_KEY). The extra
// round-trip buys one cache-update path instead of two.
async function updateProfile(fields) {
  const res = await apiFetch('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(buildProfilePatchBody(fields)),
    _silent: true,
  });
  await verifySession();
  // getUser() is the freshly re-probed row; res.user (identical shape,
  // straight off the PATCH response) is the fallback only if that probe
  // raced a session loss.
  const user = getUser() || res.user;
  window.dispatchEvent(new CustomEvent(USER_UPDATED_EVENT, { detail: { user } }));
  return user;
}

export {
  checkAuth, verifySession, getUser, isAuthenticated, logout,
  loginWithPassword, completeTwoFactorLogin, registerWithPassword,
  verifyTwoFactorSetup, disableTwoFactor, regenerateBackupCodes, setAccountPassword,
  signupStart, signupVerify, signupComplete,
  updateProfile, USER_UPDATED_EVENT,
};
